import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, Tabs, message } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import { login, register } from '../api/auth';
import { setToken, setUserInfo } from '../utils/auth';
import { useI18n } from '../i18n/I18nContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SiteFooter from '../components/SiteFooter';
import './Login.css';

const CAPTCHA_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

const generateCaptchaCode = () => {
  let code = '';
  for (let i = 0; i < 6; i++) {
    const idx = Math.floor(Math.random() * CAPTCHA_CHARS.length);
    code += CAPTCHA_CHARS[idx];
  }
  return code;
};

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [captchaCode, setCaptchaCode] = useState(generateCaptchaCode());
  const [registerForm] = Form.useForm();
  const navigate = useNavigate();
  const { t, locale } = useI18n();

  const handleLogin = async (values) => {
    setLoading(true);
    try {
      const response = await login(values);
      if (response.success) {
        message.success(t('toast.loginSuccess'));
        setToken(response.token || 'mock_token');
        setUserInfo(response.user);

        const returnToProduct = sessionStorage.getItem('returnToProduct');
        if (returnToProduct) {
          sessionStorage.removeItem('returnToProduct');
          navigate(`/product/${returnToProduct}`);
        } else {
          navigate('/');
        }
      }
    } catch (error) {
      console.error('登录失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (values) => {
    const inputCode = String(values.code || '')
      .trim()
      .toUpperCase();
    if (inputCode !== captchaCode) {
      message.error(t('toast.codeMismatch'));
      registerForm.setFieldValue('code', '');
      setCaptchaCode(generateCaptchaCode());
      return;
    }

    const payload = {
      name: values.name,
      email: values.email,
      password: values.password,
    };

    setLoading(true);
    try {
      const response = await register(payload);
      if (response.success) {
        message.success(t('toast.registerSuccess'));
        setToken(response.token || 'mock_token');
        setUserInfo(response.user);

        const returnToProduct = sessionStorage.getItem('returnToProduct');
        if (returnToProduct) {
          sessionStorage.removeItem('returnToProduct');
          navigate(`/product/${returnToProduct}`);
        } else {
          navigate('/');
        }
      }
    } catch (error) {
      console.error('注册失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshCaptcha = () => {
    registerForm.setFieldValue('code', '');
    setCaptchaCode(generateCaptchaCode());
  };

  const loginForm = (
    <Form onFinish={handleLogin} className="login-form" key={`login-${locale}`}>
      <Form.Item
        name="email"
        rules={[
          { required: true, message: t('login.emailRequired') },
          { type: 'email', message: t('login.emailInvalid') },
        ]}
      >
        <Input
          prefix={<MailOutlined />}
          placeholder={t('login.email')}
          size="large"
        />
      </Form.Item>
      <Form.Item
        name="password"
        rules={[
          { required: true, message: t('login.passwordRequired') },
          { min: 6, message: t('login.passwordMin') },
        ]}
      >
        <Input.Password
          prefix={<LockOutlined />}
          placeholder={t('login.password')}
          size="large"
        />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading} block size="large">
          {t('login.signIn')}
        </Button>
      </Form.Item>
    </Form>
  );

  const registerFormContent = (
    <Form form={registerForm} onFinish={handleRegister} className="login-form" key={`reg-${locale}`}>
      <Form.Item
        name="name"
        rules={[
          { required: true, message: t('login.nameRequired') },
          { min: 2, message: t('login.nameMin') },
        ]}
      >
        <Input
          prefix={<UserOutlined />}
          placeholder={t('login.nickname')}
          size="large"
        />
      </Form.Item>
      <Form.Item
        name="email"
        rules={[
          { required: true, message: t('login.emailRequired') },
          { type: 'email', message: t('login.emailInvalid') },
        ]}
      >
        <Input
          prefix={<MailOutlined />}
          placeholder={t('login.email')}
          size="large"
        />
      </Form.Item>
      <Form.Item>
        <Form.Item
          name="code"
          noStyle
          rules={[
            { required: true, message: t('login.codeRequired') },
            {
              pattern: /^[A-Za-z0-9]{6}$/,
              message: t('login.codeLen'),
            },
          ]}
        >
          <Input
            placeholder={t('login.code')}
            size="large"
            addonAfter={
              <Button type="link" onClick={refreshCaptcha} style={{ padding: 0 }}>
                {t('login.refreshCode')}
              </Button>
            }
          />
        </Form.Item>
        <div style={{ marginTop: 8, fontSize: 18, letterSpacing: 4, fontWeight: 600, userSelect: 'none' }}>
          {captchaCode}
        </div>
      </Form.Item>
      <Form.Item
        name="password"
        rules={[
          { required: true, message: t('login.passwordRequired') },
          { min: 6, message: t('login.passwordMin') },
        ]}
      >
        <Input.Password
          prefix={<LockOutlined />}
          placeholder={t('login.password')}
          size="large"
        />
      </Form.Item>
      <Form.Item
        name="confirmPassword"
        dependencies={['password']}
        rules={[
          { required: true, message: t('login.confirmRequired') },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error(t('login.passwordMismatch')));
            },
          }),
        ]}
      >
        <Input.Password
          prefix={<LockOutlined />}
          placeholder={t('login.confirmPassword')}
          size="large"
        />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading} block size="large">
          {t('login.signUp')}
        </Button>
      </Form.Item>
    </Form>
  );

  return (
    <div className="login-container">
      <div className="login-lang-bar">
        <LanguageSwitcher />
      </div>
      <div className="login-main">
        <Card className="login-card" title={t('appTitle')}>
          <Tabs
            items={[
              { key: '1', label: t('login.tabLogin'), children: loginForm },
              { key: '2', label: t('login.tabRegister'), children: registerFormContent },
            ]}
          />
        </Card>
      </div>
      <SiteFooter variant="login" />
    </div>
  );
};

export default Login;
